import {ChangeDetectionStrategy, Component, signal} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {
  AVAILABLE_BATCH_OPERATIONS,
  BatchOperation,
  BatchOperationEnum
} from '../../../../lib/models/batch.model';
import {BatchService} from '../../../../lib/services/batch.service';
import {
  AccessMatrixService,
  ModuleTypeEnum,
  RouteTypeEnum,
  SubModuleTypeEnum,
} from '../../../../lib/services/access-matrix.service';

interface BatchStarterForm {
  operation: FormControl<BatchOperationEnum | null>;
  targetGroups: FormControl<string>;
  parameters: FormControl<string>;
  dryRun: FormControl<boolean>;
}

@Component({
  selector: 'ms-batch-starter',
  templateUrl: './batch-starter.component.html',
  styleUrls: ['./batch-starter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BatchStarterComponent {

  public readonly availableOperations: BatchOperation[] = AVAILABLE_BATCH_OPERATIONS;

  public readonly form: FormGroup<BatchStarterForm> = new FormGroup<BatchStarterForm>({
    operation: new FormControl<BatchOperationEnum | null>(null),
    targetGroups: new FormControl<string>('', {nonNullable: true}),
    parameters: new FormControl<string>('', {nonNullable: true}),
    dryRun: new FormControl<boolean>(true, {nonNullable: true}),
  });

  public readonly results = signal<string>('');
  public readonly isExecuting = signal<boolean>(false);

  /**
   * Whether the user is allowed to run a batch with the dry-run mode disabled.
   * When false, the dry-run checkbox is forced checked and disabled.
   */
  public readonly canRunWrite: boolean;

  constructor(
    private batchService: BatchService,
    private accessMatrixService: AccessMatrixService,
  ) {
    this.canRunWrite = this.accessMatrixService.isAvailableModule(
      RouteTypeEnum.BATCH,
      ModuleTypeEnum.BATCH_OPERATION,
      SubModuleTypeEnum.BATCH_OPERATION_WRITE,
    );
    if (!this.canRunWrite) {
      this.form.controls.dryRun.setValue(true);
      this.form.controls.dryRun.disable();
    }
  }

  public get targetGroupsCount(): number {
    return this.countNonEmptyLines(this.form.controls.targetGroups.value);
  }

  public countNonEmptyLines(text: string): number {
    return this.splitNonEmptyLines(text).length;
  }

  public onOperationSelected(operationId: BatchOperationEnum): void {
    const operation: BatchOperation | undefined = this.availableOperations.find(
      (candidate: BatchOperation): boolean => candidate.id === operationId,
    );
    if (!operation) {
      return;
    }
    this.form.controls.operation.setValue(operation.id);
    this.form.controls.targetGroups.setValue(operation.defaultTargetGroups);
    this.form.controls.parameters.setValue(operation.defaultParameters);
    this.form.controls.dryRun.setValue(true);
  }

  public async execute(): Promise<void> {
    const operationId: BatchOperationEnum | null = this.form.controls.operation.value;
    if (operationId === null || this.isExecuting()) {
      return;
    }

    this.isExecuting.set(true);
    this.results.set('');

    try {
      const groupIds: string[] = this.splitNonEmptyLines(this.form.controls.targetGroups.value);
      const parameters: Record<string, unknown> = this.parseParameters();
      const dryRun: boolean = this.canRunWrite ? this.form.controls.dryRun.value : true;
      await this.batchService.execute(
        operationId,
        groupIds,
        parameters,
        dryRun,
        (line: string): void => this.appendResultLine(line),
      );
    } catch (error) {
      this.appendResultLine(`Erreur: ${(error as Error)?.message ?? error}`);
    } finally {
      this.isExecuting.set(false);
    }
  }

  private parseParameters(): Record<string, unknown> {
    const raw: string = this.form.controls.parameters.value.trim();
    if (!raw) {
      return {};
    }

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new Error('Les paramètres doivent être un objet JSON');
    }

    return parsed as Record<string, unknown>;
  }

  private appendResultLine(line: string): void {
    this.results.update((current: string): string => current ? `${current}\n${line}` : line);
  }

  private splitNonEmptyLines(text: string): string[] {
    return text
      .split('\n')
      .map((line: string): string => line.trim())
      .filter((line: string): boolean => line.length > 0);
  }
}
